import React from "react";
import type { FieldProps } from "../../../types/FormTypes";
import TimeInput, { type TimeInputProps } from "../DateTimeInput/TimeInput";
import Field from "../Field/Field";
import { useField } from "../Form/Form";

export interface TimeFieldProps
  extends Omit<FieldProps<string>, "error">,
    Omit<TimeInputProps, "value" | "onValueChange" | "onChange" | "name" | "error"> {
  error?: boolean | string;
  serialize?: {
    to: (value: string) => string | undefined;
    from: (value: string | undefined) => string;
  };
}

const defaultSerialize = {
  to: (value: string) => value || undefined,
  from: (value: string | undefined) => value ?? "",
};

const TimeField = (props: TimeFieldProps) => {
  const {
    className,
    name,
    label,
    helperText,
    required,
    disabled,
    fullWidth = false,
    value: controlledValue,
    error,
    onChange,
    onValueChange,
    serialize = defaultSerialize,
    ...otherProps
  } = props;
  const field = useField<string>({ name, value: controlledValue, onChange, onValueChange });
  const errorMessage = typeof error === "string" ? error : undefined;
  const hasError = Boolean(field.error || errorMessage || (typeof error === "boolean" ? error : false));
  const value = serialize.from(field.value);
  const handleValueChange = React.useCallback(
    (nextValue: string) => {
      field.onValueChange(serialize.to(nextValue) ?? "");
    },
    [field, serialize],
  );

  return (
    <Field
      className={className}
      name={name}
      fullWidth={fullWidth}
    >
      {label ? <Field.Label required={required ?? field.required}>{label}</Field.Label> : null}
      <Field.Control>
        <TimeInput
          ref={field.ref as any}
          value={value}
          onValueChange={handleValueChange}
          onBlur={field.onBlur}
          disabled={disabled}
          fullWidth={fullWidth}
          error={hasError}
          {...otherProps}
        />
      </Field.Control>
      <Field.Message
        error={hasError}
        message={errorMessage ?? helperText}
      />
    </Field>
  );
};

export default TimeField;
